// Single source of truth for the persisted snapshot. Wraps the repository so
// feature code can call simple mutation helpers and not worry about saves.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppSnapshot, Repository } from '@/lib/storage/repository';
import { EMPTY_SNAPSHOT } from '@/lib/storage/repository';
import { localRepository } from '@/lib/storage/localRepository';
import type { ChatMessage, ChatState, LibraryWord, Persona, Relationship, Story, Word } from '@/lib/types';
import { DEFAULT_RELATIONSHIP, bumpWarmth, noteMessageSent } from '@/lib/relationships';
import { seedChatState } from '@/features/chat/useChat';

interface AppStateApi {
  snapshot: AppSnapshot;
  ready: boolean;
  /** Words saved across all personas. */
  library: LibraryWord[];
  mastered: LibraryWord[];
  chatStates: Record<string, ChatState>;
  toggleFavorite: (personaId: string) => void;
  /** Increment the per-persona counter that tracks turns since last memory extraction. */
  bumpMemoryCounter: (personaId: string) => void;
  /** Replace facts after a successful extraction; resets the counter. */
  updatePersonaMemory: (personaId: string, facts: string[]) => void;
  setSkipAuth: (value: boolean) => void;
  setNudgeDismissed: (value: boolean) => void;
  setHasSeenWelcome: (value: boolean) => void;
  setSkippedSetup: (value: boolean) => void;
  /** Wholesale-replace the snapshot. Used by sync conflict resolution. */
  replaceSnapshot: (snapshot: AppSnapshot) => void;
  addCustomPersona: (persona: Persona) => void;
  matchPersona: (persona: Persona) => void;
  skipPersona: (persona: Persona) => void;
  resetPassedPersonas: () => void;
  saveWord: (word: Word, personaId?: string) => void;
  markMastered: (hz: string) => void;
  appendMessage: (personaId: string, msg: ChatMessage) => void;
  startChatIfMissing: (persona: Persona, story?: Story | null) => void;
  clearStoryContext: (personaId: string) => void;
  markStoryViewed: (personaId: string, storyDate: string) => void;
  bumpStoryWarmth: (personaId: string) => void;
  bumpStreakIfNewDay: () => void;
  patchProfile: (patch: Partial<AppSnapshot['profile']>) => void;
  reset: () => void;
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// For a new chat message's hz, count how many times each library word appears.
// Longer entries are matched first so that "咖啡" wins over a separate "啡"
// entry on the same span — mirrors the chat-bubble highlighter's greedy rule.
function bumpsForMessage(library: LibraryWord[], hz: string): Map<string, number> {
  const bumps = new Map<string, number>();
  if (!hz || library.length === 0) return bumps;
  const taken = new Array(hz.length).fill(false) as boolean[];
  const sorted = [...library].sort((a, b) => b.hz.length - a.hz.length);
  for (const w of sorted) {
    if (!w.hz) continue;
    let from = 0;
    let count = 0;
    while (from <= hz.length - w.hz.length) {
      const idx = hz.indexOf(w.hz, from);
      if (idx === -1) break;
      let free = true;
      for (let k = idx; k < idx + w.hz.length; k++) {
        if (taken[k]) {
          free = false;
          break;
        }
      }
      if (free) {
        for (let k = idx; k < idx + w.hz.length; k++) taken[k] = true;
        count += 1;
        from = idx + w.hz.length;
      } else {
        from = idx + 1;
      }
    }
    if (count > 0) bumps.set(w.hz, count);
  }
  return bumps;
}

function isYesterday(prev: string): boolean {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return prev === k;
}

export function useAppState(repo: Repository = localRepository): AppStateApi {
  const [snapshot, setSnapshot] = useState<AppSnapshot>(EMPTY_SNAPSHOT);
  const [ready, setReady] = useState(false);
  const dirtyRef = useRef(false);

  // Hydrate from repo. Runs on mount AND every time the repo changes (e.g.,
  // sign-in swaps localRepository → SyncedRepository). Resetting `ready` to
  // false here is critical: without it, the boot gate would briefly render
  // with the stale pre-swap snapshot and flash the ProfileSetupScreen at
  // returning users whose onboarded state only lives in the cloud.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    (async () => {
      const s = await repo.load();
      if (!cancelled) {
        setSnapshot(s);
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repo]);

  // Debounced persistence: save 500 ms after the last mutation.
  useEffect(() => {
    if (!ready) return;
    if (!dirtyRef.current) return;
    const t = window.setTimeout(() => {
      void repo.save(snapshot);
      dirtyRef.current = false;
    }, 500);
    return () => window.clearTimeout(t);
  }, [snapshot, ready, repo]);

  const update = useCallback((mut: (s: AppSnapshot) => AppSnapshot) => {
    setSnapshot((prev) => {
      dirtyRef.current = true;
      const next = mut(prev);
      // savedAt always tracks the most recent in-memory mutation so the sync
      // layer can use it for newest-wins conflict resolution.
      return next === prev ? prev : { ...next, savedAt: new Date().toISOString() };
    });
  }, []);

  const matchPersona = useCallback(
    (persona: Persona) => {
      update((s) => {
        const rel: Relationship = {
          ...DEFAULT_RELATIONSHIP,
          ...(s.relationships[persona.id] ?? {}),
          matched: true,
          warmth: 35,
          lastSeen: 'just now',
        };
        const chatStates = s.chatStates[persona.id]
          ? s.chatStates
          : { ...s.chatStates, [persona.id]: seedChatState(persona) };
        return {
          ...s,
          relationships: { ...s.relationships, [persona.id]: rel },
          chatStates,
        };
      });
    },
    [update],
  );

  const skipPersona = useCallback(
    (persona: Persona) => {
      update((s) => ({
        ...s,
        relationships: {
          ...s.relationships,
          [persona.id]: {
            ...DEFAULT_RELATIONSHIP,
            ...(s.relationships[persona.id] ?? {}),
            matched: false,
            passed: true,
          },
        },
      }));
    },
    [update],
  );

  const resetPassedPersonas = useCallback(() => {
    update((s) => {
      const next: Record<string, Relationship> = {};
      for (const [id, rel] of Object.entries(s.relationships)) {
        next[id] = { ...rel, passed: false };
      }
      return { ...s, relationships: next };
    });
  }, [update]);

  const saveWord = useCallback(
    (word: Word, personaId?: string) => {
      update((s) => {
        if (s.library.some((w) => w.hz === word.hz)) return s;
        const entry: LibraryWord = {
          hz: word.hz,
          py: word.py,
          en: word.en,
          seenCount: 1,
          addedAt: Date.now(),
          ...(personaId ? { personaId } : {}),
        };
        return { ...s, library: [entry, ...s.library] };
      });
    },
    [update],
  );

  const markMastered = useCallback(
    (hz: string) => {
      update((s) => {
        const word = s.library.find((w) => w.hz === hz);
        if (!word) return s;
        return {
          ...s,
          library: s.library.filter((w) => w.hz !== hz),
          mastered: [word, ...s.mastered],
        };
      });
    },
    [update],
  );

  const appendMessage = useCallback(
    (personaId: string, msg: ChatMessage) => {
      update((s) => {
        const prev: ChatState = s.chatStates[personaId] ?? { history: [] };
        const next: ChatState = { ...prev, history: [...prev.history, msg] };
        // Auto-bump seenCount for every library word that appears in this
        // message's hz. Counts each occurrence (e.g. "你好你好" → +2).
        const bumps = bumpsForMessage(s.library, msg.hz);
        const updatedLib =
          bumps.size > 0
            ? s.library.map((w) =>
                bumps.has(w.hz) ? { ...w, seenCount: w.seenCount + bumps.get(w.hz)! } : w,
              )
            : s.library;
        // Track warmth for every persona — curated and custom alike. Custom
        // personas always have a relationship row (seeded by addCustomPersona).
        const rel = s.relationships[personaId] ?? DEFAULT_RELATIONSHIP;
        const updatedRel = msg.from === 'me' ? noteMessageSent(rel) : rel;
        return {
          ...s,
          library: updatedLib,
          chatStates: { ...s.chatStates, [personaId]: next },
          relationships: { ...s.relationships, [personaId]: updatedRel },
        };
      });
    },
    [update],
  );

  const startChatIfMissing = useCallback(
    (persona: Persona, story?: Story | null) => {
      update((s) => {
        const existing = s.chatStates[persona.id];
        if (existing && !story) return s;
        const seeded = existing ?? seedChatState(persona);
        return {
          ...s,
          chatStates: {
            ...s.chatStates,
            [persona.id]: { ...seeded, ...(story ? { lastStory: story } : {}) },
          },
        };
      });
    },
    [update],
  );

  const clearStoryContext = useCallback(
    (personaId: string) => {
      update((s) => {
        const cur = s.chatStates[personaId];
        if (!cur || !cur.lastStory) return s;
        const { lastStory: _drop, ...rest } = cur;
        return { ...s, chatStates: { ...s.chatStates, [personaId]: rest } };
      });
    },
    [update],
  );

  const markStoryViewed = useCallback(
    (personaId: string, storyDate: string) => {
      // Tag the personaId with the batch date so the next day's stories show
      // as unwatched even if this user already saw yesterday's.
      if (!storyDate) return;
      update((s) =>
        s.viewedStories[personaId] === storyDate
          ? s
          : { ...s, viewedStories: { ...s.viewedStories, [personaId]: storyDate } },
      );
    },
    [update],
  );

  const bumpStoryWarmth = useCallback(
    (personaId: string) => {
      update((s) => {
        const rel = s.relationships[personaId] ?? DEFAULT_RELATIONSHIP;
        return { ...s, relationships: { ...s.relationships, [personaId]: bumpWarmth(rel, 4) } };
      });
    },
    [update],
  );

  const bumpStreakIfNewDay = useCallback(() => {
    update((s) => {
      const today = todayKey();
      if (s.profile.lastActiveDay === today) return s;
      const streak = s.profile.lastActiveDay && isYesterday(s.profile.lastActiveDay) ? s.profile.streak + 1 : 1;
      return { ...s, profile: { ...s.profile, streak, lastActiveDay: today } };
    });
  }, [update]);

  const patchProfile = useCallback(
    (patch: Partial<AppSnapshot['profile']>) => {
      update((s) => ({ ...s, profile: { ...s.profile, ...patch } }));
    },
    [update],
  );

  const reset = useCallback(() => {
    update(() => EMPTY_SNAPSHOT);
  }, [update]);

  const toggleFavorite = useCallback(
    (personaId: string) => {
      update((s) => {
        if (s.favorites[personaId]) {
          const { [personaId]: _drop, ...rest } = s.favorites;
          return { ...s, favorites: rest };
        }
        return { ...s, favorites: { ...s.favorites, [personaId]: true } };
      });
    },
    [update],
  );

  const bumpMemoryCounter = useCallback(
    (personaId: string) => {
      update((s) => {
        const cur = s.personaMemories[personaId];
        const next = cur
          ? { ...cur, msgsSinceExtract: cur.msgsSinceExtract + 1 }
          : { facts: [], updatedAt: new Date(0).toISOString(), msgsSinceExtract: 1 };
        return { ...s, personaMemories: { ...s.personaMemories, [personaId]: next } };
      });
    },
    [update],
  );

  const updatePersonaMemory = useCallback(
    (personaId: string, facts: string[]) => {
      update((s) => ({
        ...s,
        personaMemories: {
          ...s.personaMemories,
          [personaId]: {
            facts: facts.slice(0, 12),
            updatedAt: new Date().toISOString(),
            msgsSinceExtract: 0,
          },
        },
      }));
    },
    [update],
  );

  const setSkipAuth = useCallback(
    (value: boolean) => {
      update((s) => (s.skipAuth === value ? s : { ...s, skipAuth: value }));
    },
    [update],
  );

  const setNudgeDismissed = useCallback(
    (value: boolean) => {
      update((s) => (s.nudgeDismissed === value ? s : { ...s, nudgeDismissed: value }));
    },
    [update],
  );

  const setHasSeenWelcome = useCallback(
    (value: boolean) => {
      update((s) => (s.hasSeenWelcome === value ? s : { ...s, hasSeenWelcome: value }));
    },
    [update],
  );

  const setSkippedSetup = useCallback(
    (value: boolean) => {
      update((s) => (s.skippedSetup === value ? s : { ...s, skippedSetup: value }));
    },
    [update],
  );

  const replaceSnapshot = useCallback(
    (snapshot: AppSnapshot) => {
      update(() => snapshot);
    },
    [update],
  );

  const addCustomPersona = useCallback(
    (persona: Persona) => {
      update((s) => ({
        ...s,
        customPersonas: [persona, ...s.customPersonas],
        // Seed the chat with a greeting and auto-match so the persona shows up
        // in chats + rings immediately. The user explicitly created them; no
        // need to gate via a swipe.
        chatStates: { ...s.chatStates, [persona.id]: seedChatState(persona) },
        relationships: {
          ...s.relationships,
          [persona.id]: {
            ...DEFAULT_RELATIONSHIP,
            matched: true,
            warmth: 35,
            lastSeen: 'just now',
          },
        },
      }));
    },
    [update],
  );

  return {
    snapshot,
    ready,
    library: snapshot.library,
    mastered: snapshot.mastered,
    chatStates: snapshot.chatStates,
    toggleFavorite,
    bumpMemoryCounter,
    updatePersonaMemory,
    setSkipAuth,
    setNudgeDismissed,
    setHasSeenWelcome,
    setSkippedSetup,
    replaceSnapshot,
    addCustomPersona,
    matchPersona,
    skipPersona,
    resetPassedPersonas,
    saveWord,
    markMastered,
    appendMessage,
    startChatIfMissing,
    clearStoryContext,
    markStoryViewed,
    bumpStoryWarmth,
    bumpStreakIfNewDay,
    patchProfile,
    reset,
  };
}
